import React, { useState } from 'react'
import { NavLink, useNavigate, useLocation } from 'react-router-dom'
import { useAuth } from '../../hooks/useAuth'
import {
  LayoutDashboard, Award, GraduationCap, Package, Settings,
  LogOut, Menu, X, ChevronRight
} from 'lucide-react'
import './AppLayout.css'

const NAV_ITEMS = [
  { to: '/dashboard',      icon: LayoutDashboard, label: 'Career Map'                          },
  { to: '/certificate',    icon: Award,            label: 'TechBench Certificates'              },
  { to: '/certifications', icon: GraduationCap,    label: 'Third-Party Certifications Prep'     },
  { to: '/packages',       icon: Package,          label: 'Software Packages Prep'              },
  { to: '/settings',       icon: Settings,         label: 'Settings'                            },
]

export function AppLayout({ children }) {
  const { profile, signOut } = useAuth()
  const navigate = useNavigate()
  const location = useLocation()
  const [sidebarOpen, setSidebarOpen] = useState(false)

  // Map page uses full width — all other pages get reading width constraint
  const isMapPage = location.pathname === '/dashboard'
  const contentClass = `app-content${isMapPage ? '' : ' app-content--reading'}`

  async function handleSignOut() {
    await signOut()
    navigate('/login')
  }

  return (
    <div className="app-shell">
      {/* Mobile overlay */}
      {sidebarOpen && (
        <div
          className="sidebar-overlay"
          onClick={() => setSidebarOpen(false)}
          aria-hidden="true"
        />
      )}

      {/* Sidebar */}
      <aside className={`sidebar ${sidebarOpen ? 'sidebar--open' : ''}`} aria-label="Main navigation">
        <div className="sidebar__header">
          <NavLink to="/dashboard" className="sidebar__logo">
            <div className="sidebar__logo-mark">
              <svg width="18" height="18" viewBox="0 0 16 16" fill="none">
                <rect x="1" y="4" width="14" height="9" rx="1.5" stroke="#0a0c10" strokeWidth="1.5"/>
                <path d="M5 8h6M5 10.5h3" stroke="#0a0c10" strokeWidth="1.5" strokeLinecap="round"/>
                <path d="M6 2h4" stroke="#0a0c10" strokeWidth="1.5" strokeLinecap="round"/>
              </svg>
            </div>
            <span className="sidebar__logo-text">TechBench</span>
          </NavLink>
          <button
            className="sidebar__close"
            onClick={() => setSidebarOpen(false)}
            aria-label="Close navigation"
          >
            <X size={18} />
          </button>
        </div>

        <nav className="sidebar__nav">
          {NAV_ITEMS.map(({ to, icon: Icon, label }) => (
            <NavLink
              key={to}
              to={to}
              className={({ isActive }) =>
                `sidebar__link ${isActive ? 'sidebar__link--active' : ''}`
              }
              onClick={() => setSidebarOpen(false)}
            >
              <Icon size={17} />
              <span>{label}</span>
              <ChevronRight size={13} className="sidebar__chevron" />
            </NavLink>
          ))}
        </nav>

        <div className="sidebar__footer">
          <div className="sidebar__user">
            <div className="sidebar__avatar">
              {profile?.full_name?.[0]?.toUpperCase() || 'T'}
            </div>
            <div className="sidebar__user-info">
              <span className="sidebar__user-name">{profile?.full_name || 'Learner'}</span>
              <span className="sidebar__user-tier mono">
                T{profile?.subscription_tier || 1} · {profile?.subscription_tier >= 2 && profile?.selected_tree
                  ? profile.selected_tree.charAt(0).toUpperCase() + profile.selected_tree.slice(1)
                  : 'Foundation'}
              </span>
            </div>
          </div>
          <button
            className="sidebar__signout"
            onClick={handleSignOut}
            aria-label="Sign out"
          >
            <LogOut size={15} />
          </button>
        </div>

        <div className="sidebar__legal">
          <a href="https://techbench.it/privacy" target="_blank" rel="noreferrer">Privacy</a>
          <span className="sidebar__legal-dot" />
          <a href="https://techbench.it/terms" target="_blank" rel="noreferrer">Terms</a>
          <span className="sidebar__legal-dot" />
          <a href="https://techbench.it/user-agreement" target="_blank" rel="noreferrer">Agreement</a>
          <span className="sidebar__legal-dot" />
          <a href="https://techbench.it/refund-policy" target="_blank" rel="noreferrer">Refunds</a>
          <span className="sidebar__legal-dot" />
          <a href="https://techbench.it/copyright" target="_blank" rel="noreferrer">Copyright</a>
        </div>
      </aside>

      {/* Main */}
      <div className="app-main">
        {/* Top bar — mobile only */}
        <header className="topbar">
          <button
            className="topbar__menu"
            onClick={() => setSidebarOpen(true)}
            aria-label="Open navigation"
            aria-expanded={sidebarOpen}
          >
            <Menu size={20} />
          </button>
        </header>

        {/* Page content */}
        <main className={contentClass} id="main-content">
          {children}
        </main>
      </div>
    </div>
  )
}
